import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom";
import { createCheckoutThunk } from '../thunks/checkout';
import { updateQuantityThunk } from '../thunks/cart';
import { addToCart, clearCart } from "../actions/cart/actionCreators";
import { updateUser } from "../actions/userActions";
import orderApi from "../services/orderApi";
import config from "../config";
import { formatCurrency } from "../utils/format";

function CartPage() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<any>();

  const cart: Cart = useSelector((state: AppState) => state.cart);
  const user: User = useSelector((state: AppState) => state.currentUser);

  const [shippingFee, setShippingFee] = useState(0);
  const [productNote, setProductNote] = useState("");
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [stock, setStock] = useState<number | undefined>(undefined);
  const [stocksParamVisible, setStocksParamVisible] = useState(false);
  const [allowCOD, setAllowCOD] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'maya' | 'cod'>('maya');

  useEffect(() => {
    const productId = searchParams.get("productId");
    
    // If productId exists, fetch product from backend (secure)
    if (productId) {
      setLoadingProduct(true);
      orderApi.getProduct(productId).then((res) => {
        if (res.success && res.product) {
          const product = res.product;
          setShippingFee(product.shippingCost || 0);
          setStock(product.stock);
          if (product.notes) {
            setProductNote(product.notes);
          }
          // Use backend data for COD and stock visibility settings
          // Convert to boolean to handle string values from Google Sheets
          const productData = res.product as any;
          const allowCODBool = productData.allowCOD === true || productData.allowCOD === "yes" || productData.allowCOD === "true";
          const stockVisibleBool = productData.stockVisible === true || productData.stockVisible === "yes" || productData.stockVisible === "true";
          
          setAllowCOD(allowCODBool);
          setStocksParamVisible(stockVisibleBool);
          
          const cartProduct: Product = {
            id: product.productId,
            name: product.name,
            unitPrice: product.price,
            image: product.imageUrl || undefined,
            imageUrl: product.imageUrl || undefined,
            stock: product.stock,
            stockVisible: product.stockVisible,
            allowCOD: product.allowCOD
          };
          
          dispatch(clearCart());
          dispatch(addToCart(cartProduct));
        } else {
          setInvalidUrl(true);
        }
        setLoadingProduct(false);
        setIsInitializing(false);
      }).catch(() => {
        setInvalidUrl(true);
        setLoadingProduct(false);
        setIsInitializing(false);
      });
      return;
    }

    // Fallback: Use URL parameters directly (legacy - less secure)
    const nameParam = searchParams.get("name");
    const priceParam = searchParams.get("price");
    const imageParam = searchParams.get("image");
    const noteParam = searchParams.get("note");
    const shippingParam = searchParams.get("shipping");

    if (!nameParam || !nameParam.trim() || !priceParam) {
      setInvalidUrl(true);
      setIsInitializing(false);
      return;
    }

    const price = parseFloat(priceParam);
    if (isNaN(price) || price <= 0) {
      setInvalidUrl(true);
      setIsInitializing(false);
      return;
    }

    const shipping = shippingParam ? parseFloat(shippingParam) : 0;
    setShippingFee(isNaN(shipping) ? 0 : shipping);

    if (noteParam) {
      setProductNote(noteParam);
    }

    const product: Product = {
      name: nameParam.trim(),
      unitPrice: price,
      image: imageParam || undefined
    };

    dispatch(clearCart());
    dispatch(addToCart(product));
    setIsInitializing(false);
  }, [searchParams, dispatch]);


  const [formData, setFormData] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    phone: user.phone || "",
    address: user.address || "",
    city: user.city || "",
    province: user.province || "",
  })

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const onCheckout = async () => {
    setLoading(true)
    const buyerData = { ...user, ...formData }
    dispatch(updateUser(formData))

    if (paymentMethod === 'cod') {
      try {
        if (stock !== undefined && stock === 0) {
          alert("This product is now out of stock. Please refresh the page or choose a different product.");
          setLoading(false);
          return;
        }

        // Bypass Maya and directly create the order for COD
        const mayaRRN = 'COD-' + Date.now().toString().slice(-8);
        const orderData = {
          customerName: `${buyerData.firstName} ${buyerData.lastName}`,
          email: buyerData.email,
          phone: buyerData.phone,
          address: buyerData.address || '',
          city: buyerData.city || '',
          province: buyerData.province || '',
          items: cart.items.map(i => ({
            name: i.product.name,
            quantity: i.quantity,
            unitPrice: i.product.price || i.product.unitPrice || 0,
            totalPrice: i.totalPrice,
            imageUrl: i.product.imageUrl || i.product.image
          })),
          totalAmount: cart.totalAmount + shippingFee,
          shippingFee: shippingFee,
          paymentStatus: 'COD',
          orderStatus: 'processing',
          mayaRRN: mayaRRN,
          hostUrl: config.host_url
        };

        await orderApi.createOrder(orderData);

        // Update stock
        for (const item of cart.items) {
          if (item.product.id && mayaRRN) {
            try {
              await orderApi.updateStock(item.product.id, item.quantity, mayaRRN);
            } catch (error) {
              console.error(`Error updating stock for product ${item.product.id}:`, error);
            }
          }
        }

        // Navigate to success page
        window.location.href = `/purchase/success?id=${mayaRRN}&shipping=${shippingFee}&cod=true`;
      } catch (err) {
        console.error("COD Checkout failed:", err);
        alert("Failed to process Cash on Delivery order. Please try again.");
        setLoading(false);
      }
    } else {
      dispatch(createCheckoutThunk(cart, buyerData, shippingFee, stock))
      setLoading(false)
    }
  }

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (formData.firstName.trim().length < 2) errors.firstName = "First name must be at least 2 characters";
    
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (formData.lastName.trim().length < 2) errors.lastName = "Last name must be at least 2 characters";
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = "Please enter a valid email address";
      }
    }
    
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else {
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, '');
      const phoneRegex = /^\+?[0-9]{7,15}$/;
      if (!phoneRegex.test(cleanPhone)) {
        errors.phone = "Please enter a valid phone number";
      }
    }
    
    if (!formData.address.trim()) errors.address = "Address is required";
    if (formData.address.trim().length < 5) errors.address = "Please enter a complete address";
    
    if (!formData.city.trim()) errors.city = "City is required";
    
    if (!formData.province.trim()) errors.province = "Province is required";
    
    return errors;
  };

  const goToPayment = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    dispatch(updateUser(formData))
    setStep(3)
  }

  const handleQuantityChange = (product: Product, delta: number) => {
    const currentItem = cart.items.find(item => item.product.name === product.name)
    const newQuantity = (currentItem?.quantity || 0) + delta
    if (newQuantity < 1) return // Prevent going below 1
    // Prevent exceeding stock
    if (stock !== undefined && newQuantity > stock) return
    dispatch(updateQuantityThunk(product, newQuantity))
  }

  if (loadingProduct || isInitializing) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh" }}>
        {/* Skeleton Header */}
        <header style={{ background: "white", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <div style={{ width: "80px", height: "20px", background: "#e5e7eb", borderRadius: "4px" }}></div>
          </div>
        </header>

        {/* Skeleton Progress Steps */}
        <div style={{ background: "white", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ maxWidth: "600px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: i <= 1 ? "#d1d5db" : "#e5e7eb", flexShrink: 0 }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton Main Content */}
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "24px 16px", display: "grid", gridTemplateColumns: "1fr", gap: "24px" }}>
          <div style={{ background: "white", borderRadius: "20px", padding: "24px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
            {/* Skeleton Title */}
            <div style={{ height: "28px", background: "#e5e7eb", borderRadius: "4px", marginBottom: "20px", width: "35%" }}></div>
            
            {/* Skeleton Items */}
            <div style={{ display: "grid", gap: "12px" }}>
              {[1, 2].map((i) => (
                <div key={i} style={{ display: "flex", gap: "12px", padding: "16px", background: "#f9fafb", borderRadius: "12px", alignItems: "center" }}>
                  <div style={{ width: "60px", height: "60px", borderRadius: "10px", background: "#e5e7eb", flexShrink: 0 }}></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: "16px", background: "#e5e7eb", borderRadius: "4px", marginBottom: "8px", width: "70%" }}></div>
                    <div style={{ height: "14px", background: "#e5e7eb", borderRadius: "4px", width: "55%" }}></div>
                  </div>
                  <div style={{ width: "60px", height: "16px", background: "#e5e7eb", borderRadius: "4px" }}></div>
                </div>
              ))}
            </div>

            {/* Skeleton Summary Section */}
            <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <div style={{ width: "100px", height: "14px", background: "#e5e7eb", borderRadius: "4px" }}></div>
                <div style={{ width: "80px", height: "14px", background: "#e5e7eb", borderRadius: "4px" }}></div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <div style={{ width: "100px", height: "14px", background: "#e5e7eb", borderRadius: "4px" }}></div>
                <div style={{ width: "80px", height: "14px", background: "#e5e7eb", borderRadius: "4px" }}></div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ width: "100px", height: "18px", background: "#e5e7eb", borderRadius: "4px" }}></div>
                <div style={{ width: "80px", height: "18px", background: "#e5e7eb", borderRadius: "4px" }}></div>
              </div>
            </div>

            {/* Skeleton Button */}
            <div style={{ height: "48px", background: "#e5e7eb", borderRadius: "12px", marginTop: "20px" }}></div>
          </div>
        </div>
      </div>
    );
  }


  if (invalidUrl) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "60px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: "500px", textAlign: "center", background: "white", padding: "40px", borderRadius: "16px", boxShadow: "0 4px 6px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: "60px", marginBottom: "20px" }}>😕</div>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.5rem", fontWeight: "700", marginBottom: "10px" }}>Product Not Found</h1>
          <p style={{ color: "#374151", marginBottom: "30px" }}>The product link appears to be invalid or incomplete.</p>
        </div>
      </div>
    );
  }

  if (!isInitializing && cart.items.length === 0 && !invalidUrl && !searchParams.get("name") && !searchParams.get("productId")) {
    return (
      <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "60px 20px" }}>
        <div style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: "60px", marginBottom: "20px" }}>🛒</div>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.5rem", fontWeight: "700", marginBottom: "10px" }}>Your cart is empty</h1>
          <p style={{ color: "#374151", marginBottom: "30px" }}>Add some products to get started</p>
          <a href="/" style={{ background: "#6366f1", color: "white", padding: "14px 32px", borderRadius: "12px", textDecoration: "none", fontWeight: "600", display: "inline-block" }}>
            Browse Products
          </a>
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: "#f9fafb", minHeight: "100vh" }}>
      {/* Header - Minimal Neutral Design */}
      <header style={{ background: "white", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <span style={{ color: "#374151", fontSize: "0.9rem" }}>{cart.quantity} items</span>
          </div>
        </div>
      </header>

      {/* Progress Steps - Responsive */}
      <div style={{ background: "white", padding: "16px 20px", borderBottom: "1px solid #e5e7eb", overflowX: "auto" }}>
        <div style={{ minWidth: "300px", maxWidth: "600px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {[
            { num: 1, label: "Summary" },
            { num: 2, label: "Shipping" },
            { num: 3, label: "Payment" }
          ].map((s) => (
            <div key={s.num} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ 
                width: "28px", height: "28px", borderRadius: "50%", 
                background: step >= s.num ? "#6366f1" : "#e5e7eb",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: step >= s.num ? "white" : "#4b5563", fontWeight: "700", fontSize: "0.8rem",
                flexShrink: 0
              }}>
                {s.num}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content - Responsive Two-Column Grid */}
      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "24px 16px",
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "24px",
        }}
        className="cart-main-grid"
      >
        {/* Left Side - Steps */}
        <div style={{ gridColumn: "1" }}>
          {/* Step 1: Order Summary */}
          {/* Step 1: Order Summary */}
          {step === 1 && (
            <div style={{ background: "white", borderRadius: "20px", padding: "24px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: "700", marginBottom: "20px" }}>Order Summary</h2>
              
              <div style={{ display: "grid", gap: "12px" }}>
                {cart.items.map((item, idx) => (
                  <div key={idx} style={{ display: "flex", flexWrap: "wrap", gap: "12px", padding: "16px", background: "#f9fafb", borderRadius: "12px", alignItems: "center" }}>
                    {item.product.image ? (
                      <div style={{ width: "60px", height: "60px", borderRadius: "10px", backgroundImage: `url(${item.product.image})`, backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0 }}></div>
                    ) : (
                      <div style={{ width: "60px", height: "60px", background: "white", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", flexShrink: 0 }}>
                        📦
                      </div>
                    )}
                    <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
                      <h4 style={{ fontWeight: "600", marginBottom: "4px", fontSize: "0.95rem" }}>{item.product.name}</h4>
                      <p style={{ color: "#374151", fontSize: "0.85rem", marginBottom: productNote ? "4px" : "0" }}>{formatCurrency(item.product.unitPrice || 0)}</p>
                      {productNote && (
                        <p style={{ color: "#10b981", fontSize: "0.75rem", margin: 0, fontStyle: "italic" }}>Note: {productNote}</p>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button 
                        onClick={() => handleQuantityChange(item.product, -1)} 
                        disabled={item.quantity <= 1}
                        aria-label="Decrease quantity"
                        style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: item.quantity <= 1 ? "not-allowed" : "pointer", opacity: item.quantity <= 1 ? 0.5 : 1 }}>-</button>
                      <span style={{ fontWeight: "600", minWidth: "24px", textAlign: "center", fontSize: "0.9rem" }}>{item.quantity}</span>
                      <button 
                        onClick={() => handleQuantityChange(item.product, 1)} 
                        disabled={stock !== undefined && item.quantity >= stock}
                        aria-label="Increase quantity" 
                        style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: stock !== undefined && item.quantity >= stock ? "not-allowed" : "pointer", opacity: stock !== undefined && item.quantity >= stock ? 0.5 : 1 }}>+</button>
                    </div>
                    <div style={{ fontWeight: "700", color: "#6366f1", minWidth: "70px", textAlign: "right", fontSize: "0.95rem" }}>
                      {formatCurrency(item.totalPrice)}
                    </div>
                  </div>
                ))}
               </div>

               {/* Stock Display */}
               {stock !== undefined && (
                 <div style={{ marginTop: "16px", padding: "12px", borderRadius: "8px", background: stock === 0 ? "#fee2e2" : stocksParamVisible ? "#f0fdf4" : undefined, border: stock === 0 ? "1px solid #fca5a5" : stocksParamVisible ? "1px solid #86efac" : undefined }}>
                   {stock === 0 ? (
                     <p style={{ color: "#dc2626", fontWeight: "600", fontSize: "0.95rem", margin: 0 }}>Out of Stock</p>
                   ) : stocksParamVisible ? (
                     <p style={{ color: "#10b981", fontSize: "0.85rem", margin: 0 }}>{stock} {stock === 1 ? "item" : "items"} left</p>
                   ) : null}
                 </div>
               )}

               <button 
                 onClick={() => setStep(2)} 
                 disabled={stock === 0 || cart.items.some(item => item.quantity === 0)}
                 style={{ width: "100%", marginTop: "20px", background: stock === 0 || cart.items.some(item => item.quantity === 0) ? "#9ca3af" : "#6366f1", color: "white", padding: "14px", borderRadius: "12px", border: "none", fontSize: "1rem", fontWeight: "600", cursor: stock === 0 || cart.items.some(item => item.quantity === 0) ? "not-allowed" : "pointer" }}>
                 Continue to Shipping
               </button>
            </div>
          )}

          {/* Step 2: Shipping Info */}
          {step === 2 && (
            <div style={{ background: "white", borderRadius: "20px", padding: "24px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: "700", marginBottom: "8px" }}>Shipping Information</h2>
              <p style={{ color: "#374151", fontSize: "0.9rem", marginBottom: "20px" }}>Please provide your complete shipping details so we can deliver your order accurately and on time.</p>
              
              <div style={{ display: "grid", gap: "16px" }}>
                {/* Full Name Section */}
                <div>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "0.9rem", color: "#1a1a1a" }}>Full Name *</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <input name="firstName" aria-label="First Name" required value={formData.firstName} onChange={handleInputChange} placeholder="First Name" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.firstName ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.firstName ? "#fee2e2" : "white" }} />
                      {validationErrors.firstName ? (
                        <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.firstName}</p>
                      ) : (
                        <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>Your first name</p>
                      )}
                    </div>
                    <div>
                      <input name="lastName" aria-label="Last Name" required value={formData.lastName} onChange={handleInputChange} placeholder="Last Name" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.lastName ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.lastName ? "#fee2e2" : "white" }} />
                      {validationErrors.lastName ? (
                        <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.lastName}</p>
                      ) : (
                        <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>Your last name</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Email Section */}
                <div>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "0.9rem", color: "#1a1a1a" }}>Email Address *</label>
                  <input name="email" aria-label="Email Address" type="email" required value={formData.email} onChange={handleInputChange} placeholder="your@email.com" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.email ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.email ? "#fee2e2" : "white" }} />
                  {validationErrors.email ? (
                    <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.email}</p>
                  ) : (
                    <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>We'll send order updates to this email</p>
                  )}
                </div>

                {/* Phone Section */}
                <div>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "0.9rem", color: "#1a1a1a" }}>Phone Number *</label>
                  <input name="phone" aria-label="Phone Number" required value={formData.phone} onChange={handleInputChange} placeholder="+63 900 000 0000" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.phone ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.phone ? "#fee2e2" : "white" }} />
                  {validationErrors.phone ? (
                    <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.phone}</p>
                  ) : (
                    <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>For delivery contact purposes</p>
                  )}
                </div>

                {/* Address Section */}
                <div>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "0.9rem", color: "#1a1a1a" }}>Complete Address *</label>
                  <input name="address" aria-label="Complete Address" required value={formData.address} onChange={handleInputChange} placeholder="Street address, barangay, building, etc." style={{ padding: "12px", borderRadius: "10px", border: validationErrors.address ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.address ? "#fee2e2" : "white" }} />
                  {validationErrors.address ? (
                    <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.address}</p>
                  ) : (
                    <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>Your complete delivery address</p>
                  )}
                </div>

                {/* City and Province Section */}
                <div>
                  <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", fontSize: "0.9rem", color: "#1a1a1a" }}>Location *</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <input name="city" aria-label="City" required value={formData.city} onChange={handleInputChange} placeholder="City/Municipality" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.city ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.city ? "#fee2e2" : "white" }} />
                      {validationErrors.city ? (
                        <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.city}</p>
                      ) : (
                        <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>Your city</p>
                      )}
                    </div>
                    <div>
                      <input name="province" aria-label="Province" required value={formData.province} onChange={handleInputChange} placeholder="Province/Region" style={{ padding: "12px", borderRadius: "10px", border: validationErrors.province ? "2px solid #dc2626" : "1px solid #e5e7eb", fontSize: "1rem", width: "100%", boxSizing: "border-box", backgroundColor: validationErrors.province ? "#fee2e2" : "white" }} />
                      {validationErrors.province ? (
                        <p style={{ fontSize: "0.75rem", color: "#dc2626", marginTop: "4px" }}>{validationErrors.province}</p>
                      ) : (
                        <p style={{ fontSize: "0.75rem", color: "#374151", marginTop: "4px" }}>Your province</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "24px" }} className="cart-button-group">
                <button onClick={() => setStep(1)} style={{ padding: "14px", borderRadius: "12px", border: "1px solid #e5e7eb", background: "white", fontSize: "1rem", fontWeight: "600", cursor: "pointer" }}>
                  Back
                </button>
                <button onClick={goToPayment} style={{ background: "#6366f1", color: "white", padding: "14px", borderRadius: "12px", border: "none", fontSize: "1rem", fontWeight: "600", cursor: "pointer" }}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment - Final Order Summary */}
          {step === 3 && (
            <div style={{ background: "white", borderRadius: "20px", padding: "24px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem", fontWeight: "700", marginBottom: "20px" }}>Review Your Order</h2>
              
              {/* Mobile-Only Compact Order Total */}
              <div style={{ marginBottom: "20px", padding: "16px", background: "#f9fafb", borderRadius: "12px" }} className="order-total-mobile">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#374151", fontSize: "0.9rem" }}>{cart.quantity} items</span>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ color: "#6b7280", fontSize: "0.8rem" }}>Total: </span>
                    <span style={{ color: "#6366f1", fontWeight: "700", fontSize: "1.1rem" }}>{formatCurrency(cart.totalAmount + shippingFee)}</span>
                  </div>
                </div>
              </div>
              
              {/* Order Items Section */}
              <div style={{ marginBottom: "20px" }}>
                <h3 style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "12px", color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Order Items</h3>
                <div style={{ display: "grid", gap: "12px" }}>
                  {cart.items.map((item, idx) => (
                    <div key={idx} style={{ display: "flex", gap: "12px", padding: "12px", background: "#f9fafb", borderRadius: "12px", alignItems: "center" }}>
                      {item.product.image ? (
                        <img src={item.product.image} alt={item.product.name} style={{ width: "50px", height: "50px", borderRadius: "8px", objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: "50px", height: "50px", background: "white", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>📦</div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>{item.product.name}</div>
                        <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Qty: {item.quantity}</div>
                      </div>
                      <div style={{ fontWeight: "600", color: "#6366f1", fontSize: "0.9rem" }}>{formatCurrency(item.totalPrice)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Information Section */}
              <div style={{ marginBottom: "20px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
                <h3 style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "12px", color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Shipping Details</h3>
                <div style={{ display: "grid", gap: "8px", padding: "12px", background: "#f9fafb", borderRadius: "12px" }}>
                  <div style={{ display: "flex", gap: "8px", fontSize: "0.9rem" }}>
                    <span style={{ color: "#6b7280", minWidth: "80px" }}>Name:</span>
                    <span style={{ fontWeight: "500" }}>{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", fontSize: "0.9rem" }}>
                    <span style={{ color: "#6b7280", minWidth: "80px" }}>Email:</span>
                    <span style={{ fontWeight: "500" }}>{formData.email}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", fontSize: "0.9rem" }}>
                    <span style={{ color: "#6b7280", minWidth: "80px" }}>Phone:</span>
                    <span style={{ fontWeight: "500" }}>{formData.phone}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", fontSize: "0.9rem", alignItems: "flex-start" }}>
                    <span style={{ color: "#6b7280", minWidth: "80px" }}>Address:</span>
                    <span style={{ fontWeight: "500" }}>{formData.address}, {formData.city}, {formData.province}</span>
                  </div>
                </div>
                <button onClick={() => setStep(2)} style={{ marginTop: "8px", background: "none", border: "none", color: "#6366f1", fontSize: "0.85rem", fontWeight: "500", cursor: "pointer", padding: "4px 0" }}>
                  ✏️ Edit shipping details
                </button>
              </div>

              {/* Order Total Section */}
              <div style={{ paddingTop: "16px", borderTop: "1px solid #e5e7eb", marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.9rem" }}>
                  <span style={{ color: "#374151" }}>Subtotal</span>
                  <span style={{ fontWeight: "500" }}>{formatCurrency(cart.totalAmount)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontSize: "0.9rem" }}>
                  <span style={{ color: "#374151" }}>Shipping</span>
                  <span style={{ fontWeight: "500", color: shippingFee > 0 ? "#6366f1" : "#10b981" }}>{shippingFee > 0 ? formatCurrency(shippingFee) : "Free"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "12px", borderTop: "2px solid #6366f1", fontSize: "1.1rem", fontWeight: "700" }}>
                  <span>Total</span>
                  <span style={{ color: "#6366f1" }}>{formatCurrency(cart.totalAmount + shippingFee)}</span>
                </div>
              </div>

              {/* Payment Methods */}
              <div style={{ marginBottom: "20px" }}>
                <h3 style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "12px", color: "#374151" }}>Select Payment Method</h3>
                <div style={{ display: "grid", gap: "12px" }} className="payment-methods-grid">
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px", border: paymentMethod === 'maya' ? "2px solid #6366f1" : "1px solid #e5e7eb", borderRadius: "12px", cursor: "pointer", background: paymentMethod === 'maya' ? "#f5f3ff" : "white", transition: "all 0.2s" }}>
                    <input type="radio" name="paymentMethod" value="maya" checked={paymentMethod === 'maya'} onChange={() => setPaymentMethod('maya')} style={{ width: "18px", height: "18px", accentColor: "#6366f1" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", color: "#1f2937" }}>Pay Online</div>
                      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Credit/Debit, GCash, Bank Transfer</div>
                    </div>
                    <div style={{ fontSize: "1.5rem" }}>💳</div>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px", border: paymentMethod === 'cod' ? "2px solid #6366f1" : "1px solid #e5e7eb", borderRadius: "12px", cursor: allowCOD ? "pointer" : "not-allowed", background: paymentMethod === 'cod' ? "#f5f3ff" : "white", transition: "all 0.2s", opacity: allowCOD ? 1 : 0.5 }}>
                    <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'} onChange={() => allowCOD && setPaymentMethod('cod')} disabled={!allowCOD} style={{ width: "18px", height: "18px", accentColor: "#6366f1" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", color: "#1f2937" }}>
                        Cash on Delivery {!allowCOD && <span style={{ fontSize: "0.75rem", color: "#9ca3af", fontWeight: "400" }}>(Not available)</span>}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Pay when your order arrives</div>
                    </div>
                    <div style={{ fontSize: "1.5rem" }}>💵</div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }} className="payment-buttons-grid">
                <button 
                  onClick={() => setStep(2)} 
                  style={{ 
                    padding: "16px 24px", 
                    borderRadius: "12px", 
                    border: "1px solid #e5e7eb", 
                    background: "white", 
                    fontSize: "1rem", 
                    fontWeight: "600", 
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  Back
                </button>
                <button 
                  onClick={onCheckout} 
                  disabled={loading || (!allowCOD && paymentMethod === 'cod')} 
                  style={{ 
                    background: loading || (!allowCOD && paymentMethod === 'cod') ? "#9ca3af" : "linear-gradient(135deg, #6366f1, #8b5cf6)", 
                    color: "white", 
                    padding: "16px 24px", 
                    borderRadius: "12px", 
                    border: "none", 
                    fontSize: "1rem", 
                    fontWeight: "600", 
                    cursor: loading || (!allowCOD && paymentMethod === 'cod') ? "not-allowed" : "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {loading ? "Processing..." : "Continue"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Order Total - Desktop Only */}
        <div style={{ gridColumn: "2" }} className="order-total-desktop">
          <div style={{ background: "white", borderRadius: "20px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", position: "sticky", top: "20px", minWidth: "280px" }}>
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1rem", fontWeight: "700", marginBottom: "16px" }}>Order Total</h3>
            
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#374151", fontSize: "0.9rem" }}>
              <span>Subtotal ({cart.quantity} items)</span>
              <span>{formatCurrency(cart.totalAmount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", color: "#374151", fontSize: "0.9rem" }}>
              <span>Shipping</span>
              <span style={{ color: shippingFee > 0 ? "#6366f1" : "#10b981" }}>
                {shippingFee > 0 ? formatCurrency(shippingFee) : "Free"}
              </span>
            </div>
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "12px", display: "flex", justifyContent: "space-between", fontSize: "1.1rem", fontWeight: "700" }}>
              <span>Total</span>
              <span style={{ color: "#6366f1" }}>{formatCurrency(cart.totalAmount + shippingFee)}</span>
            </div>

            <div style={{ marginTop: "20px", padding: "14px", background: "#f9fafb", borderRadius: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <i className="fas fa-shield-alt" style={{ color: "#10b981", fontSize: "0.9rem" }}></i>
                <span style={{ fontSize: "0.8rem", fontWeight: "600" }}>Secure Checkout</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <i className="fas fa-undo" style={{ color: "#10b981", fontSize: "0.9rem" }}></i>
                <span style={{ fontSize: "0.8rem", fontWeight: "600" }}>30-Day Money Back</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fas fa-lock" style={{ color: "#10b981", fontSize: "0.9rem" }}></i>
                <span style={{ fontSize: "0.8rem", fontWeight: "600" }}>SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Responsive CSS */}
      <style>{`
        @media (min-width: 768px) {
          .cart-main-grid {
            grid-template-columns: 2fr 1fr !important;
          }
          .order-total-mobile {
            display: none !important;
          }
        }
        @media (max-width: 767px) {
          .cart-main-grid {
            grid-template-columns: 1fr !important;
          }
          .order-total-desktop {
            display: none !important;
          }
        }
      `}</style>

      {/* Footer */}
      <footer style={{ textAlign: "center", padding: "24px", color: "#6b7280", fontSize: "0.8rem" }}>
        <p>{config.branding.footerText}</p>
      </footer>
    </div>
  );
}

export default CartPage;
